/**
 * Preparar una imagen elegida por el usuario antes de subirla.
 *
 * El archivo que llega del teléfono es una foto de 3 o 4 MB de 4000px de lado,
 * y donde termina es en un círculo de 96px arriba de la página del negocio. Sin
 * achicarla, esa foto se descarga entera cada vez que un cliente abre la página
 * para sacar un turno — desde el teléfono, con datos, antes de ver nada. Es la
 * primera imagen de la primera pantalla, así que es exactamente la que no puede
 * tardar.
 *
 * Se hace en el navegador y no en el servidor porque el backend no tiene ningún
 * procesamiento de imágenes y agregarlo (sharp, un buffer de 4 MB por request,
 * memoria en Render) es mucho más de lo que hace falta: el canvas ya está en la
 * pantalla donde se elige el archivo.
 */

/** Lo que acepta el input y lo que se revisa acá: son la misma lista. */
export const FORMATOS_ACEPTADOS = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * El tope es del archivo ORIGINAL, antes de achicar.
 *
 * No está para cuidar el ancho de banda —lo que se sube es la versión chica—
 * sino para cortar temprano: leer un archivo de 40 MB en un canvas cuelga la
 * pestaña en un teléfono, y el error que se ve es "la página no responde", que
 * no le dice a nadie que el problema era el tamaño de la foto.
 */
export const PESO_MAXIMO = 10 * 1024 * 1024;

/** El lado del cuadrado final, en píxeles. */
export const LADO_POR_DEFECTO = 512;

/**
 * El motivo por el que este archivo no sirve, o `null` si sirve.
 *
 * Devuelve el texto ya escrito para mostrar: el que llama no tiene que traducir
 * un código de error a una frase, que es como terminan apareciendo tres
 * mensajes distintos para el mismo problema.
 */
export const revisarImagen = (archivo) => {
  if (!archivo) return 'No se eligió ningún archivo.';

  if (!FORMATOS_ACEPTADOS.includes(archivo.type)) {
    return 'Tiene que ser una imagen JPG, PNG o WEBP.';
  }

  if (archivo.size > PESO_MAXIMO) {
    const mb = Math.round(PESO_MAXIMO / (1024 * 1024));
    return `La imagen pesa demasiado. Probá con una de hasta ${mb} MB.`;
  }

  return null;
};

/**
 * La imagen achicada a un cuadrado, lista para subir.
 *
 * Recorta al centro en vez de deformar o dejar bandas: donde se muestra es en
 * un avatar redondo con `object-cover` (components/business/HeroNegocio.jsx),
 * o sea que el recorte al cuadrado pasa igual al dibujarla. Haciéndolo acá, lo
 * que se guarda es lo mismo que se ve, y no una imagen con partes que ningún
 * lugar de la app llega a mostrar.
 *
 *
 * POR QUÉ EL FONDO SE PINTA DE BLANCO
 *
 * Un PNG con fondo transparente —que es como viene la mayoría de los logos— no
 * se ve "sin fondo" en la página: se ve con lo que haya atrás. Y atrás está la
 * franja con el color del negocio, contra la que el avatar monta a medias, así
 * que el logo queda mitad sobre el color y mitad sobre el blanco de la tarjeta,
 * partido por la mitad. Un fondo blanco liso es el mismo en todas partes, y no
 * depende de qué color eligió el negocio ni de dónde se muestre la imagen.
 *
 * Se pinta ANTES de dibujar la foto, no después: el orden importa porque el
 * canvas dibuja encima de lo que ya hay.
 *
 * El PNG igual se mantiene PNG en vez de pasar todo a JPG: un logo son líneas y
 * texto con bordes duros, y el JPG los ensucia con manchitas alrededor. Las
 * fotos, que es lo otro que se sube acá, salen JPG y pesan una fracción.
 */
export const achicarImagen = (
  archivo,
  { lado = LADO_POR_DEFECTO, calidad = 0.85, fondo = '#ffffff' } = {}
) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(archivo);
    const imagen = new Image();

    imagen.onload = () => {
      // Se libera apenas la imagen quedó decodificada: si no, el archivo entero
      // se queda en memoria hasta que se recargue la página, y elegir una foto
      // cinco veces seguidas hasta encontrar la que gusta deja cinco copias.
      URL.revokeObjectURL(url);

      const origen = Math.min(imagen.naturalWidth, imagen.naturalHeight);
      if (!origen) {
        reject(new Error('No pudimos leer la imagen.'));
        return;
      }

      // Nunca se agranda: estirar una imagen de 80px a 512 no le agrega
      // detalle, sólo la vuelve borrosa y pesada.
      const destino = Math.min(lado, origen);

      const lienzo = document.createElement('canvas');
      lienzo.width = destino;
      lienzo.height = destino;

      const pincel = lienzo.getContext('2d');
      if (!pincel) {
        reject(new Error('No pudimos procesar la imagen.'));
        return;
      }

      // El fondo primero, tapando todo el cuadrado. Lo que la imagen tenga de
      // opaco lo va a pisar igual; lo transparente es lo que queda blanco.
      pincel.fillStyle = fondo;
      pincel.fillRect(0, 0, destino, destino);

      // El cuadrado del centro del original, escalado al del canvas.
      pincel.drawImage(
        imagen,
        (imagen.naturalWidth - origen) / 2,
        (imagen.naturalHeight - origen) / 2,
        origen,
        origen,
        0,
        0,
        destino,
        destino
      );

      const tipo = archivo.type === 'image/png' ? 'image/png' : 'image/jpeg';

      lienzo.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('No pudimos procesar la imagen.'))),
        tipo,
        calidad
      );
    };

    imagen.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('El archivo no es una imagen que podamos abrir.'));
    };

    imagen.src = url;
  });

/** La extensión que le corresponde al blob que devuelve `achicarImagen`. */
export const extensionDe = (blob) => (blob?.type === 'image/png' ? 'png' : 'jpg');
